import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const lines = ["Digital identity,", "made in Africa,", "for Africa."];

const MaskedLine = ({ children, delay, entered, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={entered ? { y: 0 } : {}}
      transition={{ duration: 1, delay, ease: [0.65, 0, 0.35, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = ({ entered }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-t from-canvas/80 via-transparent to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 px-6 sm:px-12 pb-20 pt-40">
        <div className="mb-10 flex items-center gap-4">
          <span className="h-px w-16 bg-clay" />
          <MaskedLine delay={0.2} entered={entered} className="text-xs uppercase tracking-[0.3em] font-bold text-gold">
            Open-source · Sovereign · Pan-African
          </MaskedLine>
        </div>

        <h1 data-testid="hero-heading" className="font-display font-bold text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[1.02] max-w-6xl">
          {lines.map((line, i) => (
            <MaskedLine key={line} delay={0.35 + i * 0.15} entered={entered}>
              {i === 1 ? (
                <>
                  made in <span className="text-clay">Africa</span>,
                </>
              ) : (
                line
              )}
            </MaskedLine>
          ))}
        </h1>

        <div className="mt-14 flex flex-col lg:flex-row lg:items-end justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={entered ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 1, ease: [0.65, 0, 0.35, 1] }}
            className="max-w-xl text-base md:text-lg text-ink-dim leading-relaxed"
          >
            Adulis is a modular, open-source identity platform — inspired by MOSIP, engineered in
            Ethiopia. Foundational digital public infrastructure that African nations can own,
            adapt, and trust.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={entered ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.65, 0, 0.35, 1] }}
            className="flex items-center gap-6"
          >
            <a href="#manifesto" data-testid="hero-cta" className="btn-pill btn-pill-solid">
              Read the manifesto
            </a>
            <a href="#platform" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-ink-dim hover:text-ink transition-colors duration-300">
              <ArrowDown size={14} /> Explore
            </a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
